import React, { useEffect } from "react"
import { useLocation } from "react-router-dom"
import { useWatch } from "antd/es/form/Form"
import PropTypes from "prop-types"
import { Card } from "ui/Card"
import { Form, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { MultipleSelectWithAddItemOption } from "ui/MultipleSelect/MultipleSelectWithAddItemOption"
import { CheckableTag } from "ui/CheckableTag"
import { TRIP_LANGUAGES, SKILL_LEVELS } from "constants/experiences"
import { useSelector } from "react-redux"

export const ExperienceDetails = ({ form, setSelectedLanguages, selectedLanguages, selectedSkillLevels, setSelectedSkillLevels, onLanguageInteraction, onSkillInteraction }) => {
  const [t] = useTranslation()
  const location = useLocation();
  const { categories } = useSelector((state) => state.trips)
  const { tripCategories } = useSelector((state) => state.trips)
  const isEditMode = location.pathname.includes("/edit/")
  const formLanguages = useWatch("languages", form)
  const formSkillLevels = useWatch("skill_levels", form)
  const watchedIncludedItems = useWatch("included_items", form) || []
  const watchedBringItems = useWatch("bring_items", form) || []

  useEffect(() => {
    if (!formLanguages || formLanguages.length === 0) {
      const defaultLang = TRIP_LANGUAGES.find((l) => l.value === "english")
      if (defaultLang) {
        setSelectedLanguages([defaultLang.value])
      }
    } else {
      setSelectedLanguages(formLanguages)
    }

    if (!formSkillLevels || formSkillLevels.length === 0) {
      const defaultSkill = SKILL_LEVELS.find((s) => s.value === "beginner")
      if (defaultSkill && !isEditMode) {
        setSelectedSkillLevels([defaultSkill.value])
      }
    } else {
      setSelectedSkillLevels(formSkillLevels)
    }
  }, [formLanguages, formSkillLevels])

  const handleLanguageChange = (language, checked) => {
    const newSelectedLanguages = checked
      ? [...selectedLanguages, language.value]
      : selectedLanguages.filter((l) => l !== language.value)

    setSelectedLanguages(newSelectedLanguages)
    onLanguageInteraction?.()
  }

  const handleSkillLevelChange = (level, checked) => {
    const newSelectedSkillLevels = checked
      ? [...selectedSkillLevels, level.value]
      : selectedSkillLevels.filter((l) => l !== level.value)

    setSelectedSkillLevels(newSelectedSkillLevels)
    onSkillInteraction?.()
  }

  const handleIncludedItemsChange = (values) => {
    form.setFieldsValue({ included_items: values })
  }

  const handleBringItemsChange = (values) => {
    form.setFieldsValue({ bring_items: values })
  }

  return (
    <Card title={t("experiences.details.experience_details")} className="mb-3xl">
      <Form.Item name="included_items" label={t("experiences.details.labels.trip_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={tripCategories?.data?.items}
          onChange={handleIncludedItemsChange}
          loading={categories?.loading}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedBringItems} />
      </Form.Item>

      <Form.Item name="bring_items" label={t("experiences.details.labels.guest_items")}>
        <MultipleSelectWithAddItemOption
          placeholder={t("experiences.details.placeholders.select_or_type")}
          options={tripCategories?.data?.items}
          loading={categories?.loading}
          onChange={handleBringItemsChange}
          addItemLabel={t("experiences.details.labels.add_item")}
          notFoundLabel={t("experiences.details.labels.not_found")}
          disabledOptions={watchedIncludedItems}
        />
      </Form.Item>

      <Form.Item name="languages" label={t("experiences.details.labels.languages")}>
        <Flex wrap gap="small">
          {TRIP_LANGUAGES.map((language) => (
            <CheckableTag
              key={language.value}
              className="mr-xs mb-xxs"
              tag={language.label}
              checked={selectedLanguages.includes(language.value)}
              onChange={(checked) => handleLanguageChange(language, checked)}
            />
          ))}
        </Flex>
      </Form.Item>

      <Form.Item name="skill_levels" label={t("experiences.details.labels.skill_level")}>
        <Flex wrap gap="small">
          {SKILL_LEVELS.map((level) => (
            <CheckableTag
              key={level.value}
              className="mr-xs mb-xxs"
              tag={level.label}
              checked={selectedSkillLevels.includes(level.value)}
              onChange={(checked) => handleSkillLevelChange(level, checked)}
            />
          ))}
        </Flex>
      </Form.Item>
    </Card>
  )
}

ExperienceDetails.propTypes = {
  form: PropTypes.object.isRequired,
  selectedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedLanguages: PropTypes.func.isRequired,
  selectedSkillLevels: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedSkillLevels: PropTypes.func.isRequired,
  onLanguageInteraction: PropTypes.func,
  onSkillInteraction: PropTypes.func,
}
